import { useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Collapse, Slider } from 'antd';
import { UpOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import {
  COLOR_PALETTE_NAMES,
  type ColorPaletteName,
} from '../../../../constants/color';
import type { HexColor } from '../../../../utils/color';
import { useMapColorPalette } from '../../../../hooks/SimViewer/useMapColorPalette';

type Props = {
  count: number;
};

const ColorSwitcher = ({ count }: Props): ReactNode => {
  const { t } = useTranslation();
  const {
    mapColorPalette,
    mapColorOpacity,
    getHexColors,
    setMapColorOpacity,
    setMapColorPalette,
  } = useMapColorPalette();

  const mapColors = useMemo((): Record<ColorPaletteName, HexColor[]> => {
    return COLOR_PALETTE_NAMES.reduce(
      (
        colors: Record<ColorPaletteName, HexColor[]>,
        name: ColorPaletteName
      ) => {
        return {
          ...colors,
          [name]: getHexColors(count || 'all', name),
        };
      },
      {} as Record<ColorPaletteName, HexColor[]>
    );
  }, [count, getHexColors]);

  return (
    <StyledCollapse
      defaultActiveKey={[]}
      ghost
      expandIconPosition="end"
      expandIcon={({ isActive }) => <UpOutlined rotate={isActive ? 0 : 180} />}
    >
      <Collapse.Panel
        key="color"
        header={
          <ColorPreview>
            {mapColors[mapColorPalette].map(
              (color: HexColor, index: number): ReactNode => (
                <ColorBar key={index} style={{ backgroundColor: color }} />
              )
            )}
          </ColorPreview>
        }
      >
        <OpacityContainer>
          <Slider
            value={mapColorOpacity * 100}
            min={0}
            max={100}
            step={10}
            marks={{
              0: {
                style: { fontSize: 12, color: '#000000D9' },
                label: '0%',
              },
              100: {
                style: { fontSize: 12, color: '#000000D9' },
                label: '100%',
              },
            }}
            tooltip={{
              formatter: (value: number | undefined): ReactNode => {
                return `${value ?? '-'}%`;
              },
            }}
            handleStyle={{
              width: 12,
              height: 12,
              marginTop: -4,
              borderColor: 'var(--primary-color)',
            }}
            railStyle={{ backgroundColor: '#E0E0E0' }}
            trackStyle={{ backgroundColor: 'var(--primary-color)' }}
            onChange={(value: number): void => {
              setMapColorOpacity(value / 100);
            }}
          />
          <SliderLabel>{t('opacity')}</SliderLabel>
        </OpacityContainer>
        <ColorList>
          {COLOR_PALETTE_NAMES.map((name: ColorPaletteName) => {
            return (
              <ColorButton
                key={name}
                type="text"
                onClick={(): void => {
                  setMapColorPalette(name);
                }}
              >
                <ColorPreview data-selected={name === mapColorPalette}>
                  {mapColors[name].map(
                    (color: HexColor, index: number): ReactNode => (
                      <ColorBar
                        key={index}
                        style={{ backgroundColor: color }}
                      />
                    )
                  )}
                </ColorPreview>
              </ColorButton>
            );
          })}
        </ColorList>
      </Collapse.Panel>
    </StyledCollapse>
  );
};

const OpacityContainer = styled.div`
  position: relative;
  width: 85%;
  margin: 0 auto;
`;

const SliderLabel = styled.span`
  position: absolute;
  top: 15px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 12px;
  color: #000000d9;
  pointer-events: none;
`;

const ColorList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const StyledCollapse = styled(Collapse)`
  width: 200px;
  margin-left: auto;
  .ant-collapse-item:last-child > .ant-collapse-header {
    border-radius: 2px;
  }
  .ant-collapse-item:last-child.ant-collapse-item-active
    > .ant-collapse-header {
    border-radius: 2px 2px 0 0;
  }
  .ant-collapse-item > .ant-collapse-header {
    background-color: #d9d9d9a6;
    padding: 6px 28px 6px 8px;
    :hover {
      background-color: #d9d9d9;
    }
  }
  .ant-collapse-content > .ant-collapse-content-box {
    padding: 0;
    border-radius: 0 0 2px 2px;
  }
  .ant-collapse-item > .ant-collapse-content > .ant-collapse-content-box {
    padding-top: 8px;
    padding-bottom: 8px;
    background-color: #d9d9d933;
  }
  .ant-collapse-item > .ant-collapse-header .ant-collapse-arrow {
    position: absolute;
    top: 50%;
    right: 8px;
    left: auto;
    margin: 0;
    transform: translateY(-50%);
  }
`;

const ColorPreview = styled.div<{ 'data-selected'?: boolean }>`
  display: flex;
  flex-direction: row;
  width: 100%;
  height: 20px;
  padding: 2px;
  box-sizing: border-box;
  border: 2px solid
    ${(props) =>
      props['data-selected'] ? 'var(--primary-color)' : 'transparent'};
`;

const ColorButton = styled(Button)`
  width: 100%;
  height: 20px;
  display: flex;
  align-items: center;
  padding: 4px 8px;
  box-sizing: border-box;
  border-radius: 2px;
  cursor: pointer;
  outline: none;
  background-color: transparent;
  &:hover {
    opacity: 0.8;
  }
`;

const ColorBar = styled.div`
  flex: 1;
  height: 100%;
`;

export { ColorSwitcher };
